import { useEffect, useState } from "react";
import { Navigate } from "react-router-dom";
import { motion } from "framer-motion";
import { Navbar } from "../components/Navbar";
import { useAuth } from "../context/AuthContext";
import api from "../lib/api";
import { toast } from "sonner";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "../components/ui/table";
import { Button } from "../components/ui/button";
import { downloadCsv } from "../lib/csv";
import { DollarSign, ShoppingCart, CheckCircle2, Loader2, Receipt, Mail, MailCheck, Download, TrendingUp, Users, Target, ThumbsUp, Clock, MessageSquareText, Send, Plane, RefreshCw, Award } from "lucide-react";
import { Input } from "../components/ui/input";
import { Switch } from "../components/ui/switch";
import { getIcon } from "../lib/icons";

const STATUS_STYLES = {
  COMPLETED: "text-primary border-primary/40 bg-primary/10",
  CREATED: "text-muted-foreground border-border bg-secondary/40",
  FAILED: "text-destructive border-destructive/40 bg-destructive/10",
};

export default function Admin() {
  const { user, ready } = useAuth();
  const [data, setData] = useState(null);
  const [overview, setOverview] = useState(null);
  const [activation, setActivation] = useState(null);
  const [quizAnalytics, setQuizAnalytics] = useState(null);
  const [badgeStats, setBadgeStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [gmail, setGmail] = useState(null);
  const [analytics, setAnalytics] = useState(null);
  const [feedback, setFeedback] = useState(null);
  const [drip, setDrip] = useState(null);
  const [dripEmail, setDripEmail] = useState("");
  const [dripSending, setDripSending] = useState(false);
  const [dripRefreshedAt, setDripRefreshedAt] = useState(null);
  const [dripRefreshing, setDripRefreshing] = useState(false);
  const [dripAuto, setDripAuto] = useState(false);

  const refreshDrip = async () => {
    setDripRefreshing(true);
    try {
      const r = await api.get("/admin/drip/status");
      setDrip(r.data);
      setDripRefreshedAt(new Date());
    } catch {
      /* ignore */
    } finally {
      setDripRefreshing(false);
    }
  };

  useEffect(() => {
    if (!dripAuto) return;
    const id = setInterval(refreshDrip, 30000);
    return () => clearInterval(id);
  }, [dripAuto]);

  useEffect(() => {
    api.get("/admin/transactions").then((r) => setData(r.data)).catch(() => {}).finally(() => setLoading(false));
    api.get("/admin/overview").then((r) => setOverview(r.data)).catch(() => {});
    api.get("/admin/activation").then((r) => setActivation(r.data)).catch(() => {});
    api.get("/admin/lesson-quiz-analytics").then((r) => setQuizAnalytics(r.data)).catch(() => {});
    api.get("/admin/badges").then((r) => setBadgeStats(r.data)).catch(() => {});
    api.get("/admin/gmail-status").then((r) => setGmail(r.data)).catch(() => {});
    api.get("/admin/lesson-analytics").then((r) => setAnalytics(r.data)).catch(() => {});
    api.get("/admin/feedback").then((r) => setFeedback(r.data)).catch(() => {});
    api.get("/admin/drip/status").then((r) => { setDrip(r.data); setDripRefreshedAt(new Date()); }).catch(() => {});
  }, []);

  const sendTestSequence = async () => {
    setDripSending(true);
    try {
      const r = await api.post("/admin/drip/test", { email: dripEmail || undefined });
      toast.success(`Test sequence sent to ${r.data.sent_to}`, {
        description: "Day 0 welcome, Day 3 nudge, and Day 7 next-step were dispatched. Check that inbox.",
      });
    } catch (e) {
      toast.error(e.response?.data?.detail || "Could not send the test sequence.");
    } finally {
      setDripSending(false);
    }
  };

  const exportCsv = () => {
    downloadCsv(
      `dronacademy-transactions-${new Date().toISOString().slice(0, 10)}.csv`,
      [
        { label: "Date", value: (r) => r.created_at },
        { label: "Customer", value: (r) => r.user_name },
        { label: "Email", value: (r) => r.user_email },
        { label: "Order ID", key: "order_id" },
        { label: "Amount", value: (r) => r.captured_amount || r.amount },
        { label: "Currency", key: "currency" },
        { label: "Campaign", value: (r) => r.campaign || "" },
        { label: "UTM Source", value: (r) => r.utm_source || "" },
        { label: "GCLID", value: (r) => r.gclid || "" },
        { label: "Status", key: "status" },
      ],
      data?.transactions || []
    );
  };

  if (ready && user && user.role !== "admin") return <Navigate to="/dashboard" replace />;

  const fmtDate = (iso) => {
    if (!iso) return "—";
    try {
      return new Date(iso).toLocaleString(undefined, { dateStyle: "medium", timeStyle: "short" });
    } catch {
      return iso;
    }
  };

  const stats = data
    ? [
        { label: "Revenue", value: `$${data.summary.revenue.toFixed(2)}`, icon: DollarSign },
        { label: "Completed", value: data.summary.completed, icon: CheckCircle2 },
        { label: "Total Orders", value: data.summary.total_orders, icon: ShoppingCart },
        { label: "Total Leads", value: overview ? overview.total_leads : "—", icon: Users },
        { label: "Lead Conversion", value: overview ? `${overview.lead_conversion_pct}%` : "—", icon: Target },
      ]
    : [];

  const funnel = overview
    ? Object.entries(overview.campaigns)
        .map(([camp, r]) => ({ camp, ...r }))
        .sort((a, b) => b.revenue - a.revenue)
    : [];

  return (
    <div className="min-h-screen pb-16">
      <Navbar />
      <main className="max-w-6xl mx-auto px-4 sm:px-6 pt-8">
        <div className="flex items-center gap-3 mb-2">
          <Receipt className="h-6 w-6 text-primary" />
          <span className="text-xs font-mono uppercase tracking-[0.3em] text-primary">Command Center</span>
        </div>
        <h1 className="font-display text-3xl md:text-4xl font-bold mb-8">Transactions</h1>

        <div className="flex justify-end mb-4">
          <Button onClick={exportCsv} variant="outline" disabled={!data || data.transactions.length === 0}
            className="rounded-sm" data-testid="export-transactions-csv">
            <Download className="h-4 w-4 mr-2" /> Export CSV
          </Button>
        </div>

        <div className="p-5 rounded-sm border border-border/70 bg-card mb-8 flex flex-col sm:flex-row sm:items-center gap-4" data-testid="email-status-card">
          <div className={`h-11 w-11 rounded-sm flex items-center justify-center shrink-0 ${gmail?.connected ? "bg-primary/15" : "bg-secondary"}`}>
            {gmail?.connected ? <MailCheck className="h-6 w-6 text-primary" /> : <Mail className="h-6 w-6 text-muted-foreground" />}
          </div>
          <div className="flex-1">
            <h3 className="font-display font-bold">Receipt Emails</h3>
            <p className="text-sm text-muted-foreground">
              {gmail?.connected
                ? `Active — receipts are sent via Resend from ${gmail.sender}.`
                : "Email is not configured. Add a RESEND_API_KEY to enable purchase receipts."}
            </p>
          </div>
          {gmail?.connected && (
            <span className="inline-flex items-center gap-1.5 text-xs font-mono uppercase tracking-wider text-primary border border-primary/40 bg-primary/10 px-3 py-1.5 rounded-sm" data-testid="email-status-badge">
              <MailCheck className="h-4 w-4" /> Resend connected
            </span>
          )}
        </div>

        <div className="p-5 rounded-sm border border-border/70 bg-card mb-8" data-testid="drip-card">
          <div className="flex items-start justify-between gap-3 mb-1">
            <div className="flex items-center gap-2">
              <Plane className="h-4 w-4 text-primary" />
              <h3 className="font-display font-bold">Founding Pilot Onboarding Emails</h3>
            </div>
            <div className="flex items-center gap-3 shrink-0">
              {dripRefreshedAt && (
                <span className="text-[11px] font-mono text-muted-foreground hidden sm:inline" data-testid="drip-refreshed-at">
                  Updated {dripRefreshedAt.toLocaleTimeString(undefined, { hour: "2-digit", minute: "2-digit", second: "2-digit" })}
                </span>
              )}
              <label className="flex items-center gap-1.5 cursor-pointer select-none" data-testid="drip-auto-toggle-label">
                <Switch checked={dripAuto} onCheckedChange={setDripAuto} data-testid="drip-auto-toggle" />
                <span className="text-[11px] font-mono uppercase tracking-wider text-muted-foreground">Auto 30s</span>
              </label>
              <Button onClick={refreshDrip} variant="outline" size="sm" disabled={dripRefreshing}
                className="rounded-sm h-8 px-3" data-testid="drip-refresh-button">
                <RefreshCw className={`h-3.5 w-3.5 ${dripRefreshing ? "animate-spin" : ""}`} />
                <span className="ml-1.5 text-xs">Refresh</span>
              </Button>
            </div>
          </div>
          <p className="text-xs text-muted-foreground mb-4">
            Automated drip via Resend: <strong>Day 0</strong> welcome (at signup) → <strong>Day 3</strong> momentum nudge (skipped if the Golden Five is already complete) → <strong>Day 7</strong> next-step (subject adapts to completion). Runs on a background scheduler.
          </p>
          <div className="grid grid-cols-2 sm:grid-cols-4 lg:grid-cols-8 gap-3 mb-5">
            {(() => {
              const enrolled = drip?.enrolled ?? 0;
              const unsub = drip?.unsubscribed ?? 0;
              const optOutPct = enrolled > 0 ? Math.round((unsub / enrolled) * 100) : null;
              const g5Pct = drip?.golden_five_completion_pct;
              return [
                { label: "Enrolled", value: drip?.enrolled ?? "—",
                  badge: optOutPct == null ? null : `${optOutPct}% opt-out`, badgeTone: "warn" },
                { label: "Golden Five Complete", value: drip?.golden_five_completed ?? "—",
                  badge: g5Pct == null ? null : `${g5Pct}% complete`,
                  badgeTone: g5Pct == null ? null : (g5Pct >= 40 ? "good" : "warn"),
                  highlight: true },
                { label: "Day 3 Pending", value: drip?.day3_pending ?? "—" },
                { label: "Day 3 Sent", value: drip?.day3_sent ?? "—" },
                { label: "Day 3 Skipped", value: drip?.day3_skipped_already_complete ?? "—" },
                { label: "Day 7 Pending", value: drip?.day7_pending ?? "—" },
                { label: "Day 7 Sent", value: drip?.day7_sent ?? "—" },
                { label: "Unsubscribed", value: drip?.unsubscribed ?? "—" },
              ];
            })().map((s) => (
              <div key={s.label} className={`p-3 rounded-sm border ${s.highlight ? "border-primary/40 bg-primary/5" : "border-border/60 bg-secondary/30"}`} data-testid={`drip-stat-${s.label.toLowerCase().replace(/ /g, "-")}`}>
                <div className="flex items-center gap-1.5 flex-wrap">
                  <span className={`font-mono text-xl font-bold ${s.highlight ? "text-primary" : ""}`}>{s.value}</span>
                  {s.badge && (
                    <span className={`text-[9px] font-mono uppercase tracking-wider border px-1.5 py-0.5 rounded-sm ${s.badgeTone === "warn" ? "text-amber-400 border-amber-400/40 bg-amber-400/10" : "text-primary border-primary/40 bg-primary/10"}`} data-testid={s.highlight ? "drip-completion-badge" : "drip-optout-badge"}>
                      {s.badge}
                    </span>
                  )}
                </div>
                <div className="text-[10px] uppercase tracking-widest text-muted-foreground mt-1">{s.label}</div>
              </div>
            ))}
          </div>
          <div className="rounded-sm border border-border/60 bg-secondary/20 p-4 mb-4" data-testid="lead-nurture-block">
            <div className="flex items-center gap-2 mb-3">
              <Mail className="h-4 w-4 text-primary" />
              <span className="text-sm font-semibold">Lead Nurture Series</span>
              <span className="text-xs text-muted-foreground">— instant confirmation + 3 follow-ups; stops when a lead creates an account.</span>
            </div>
            <div className="grid grid-cols-2 sm:grid-cols-4 lg:grid-cols-5 gap-3">
              {[
                { label: "Leads Enrolled", value: drip?.lead_nurture?.leads_enrolled ?? "—" },
                { label: "Nurturing", value: drip?.lead_nurture?.nurturing ?? "—" },
                { label: "Converted", value: drip?.lead_nurture?.converted ?? "—",
                  badge: drip?.lead_nurture?.conversion_pct == null ? null : `${drip.lead_nurture.conversion_pct}% converted`,
                  highlight: true },
                { label: "Unsubscribed", value: drip?.lead_nurture?.unsubscribed ?? "—" },
              ].map((s) => (
                <div key={s.label} className={`p-3 rounded-sm border ${s.highlight ? "border-primary/40 bg-primary/5" : "border-border/60 bg-secondary/30"}`} data-testid={`lead-stat-${s.label.toLowerCase().replace(/ /g, "-")}`}>
                  <div className="flex items-center gap-1.5 flex-wrap">
                    <span className={`font-mono text-xl font-bold ${s.highlight ? "text-primary" : ""}`}>{s.value}</span>
                    {s.badge && (
                      <span className="text-[9px] font-mono uppercase tracking-wider text-primary border border-primary/40 bg-primary/10 px-1.5 py-0.5 rounded-sm" data-testid="lead-conversion-badge">{s.badge}</span>
                    )}
                  </div>
                  <div className="text-[10px] uppercase tracking-widest text-muted-foreground mt-1">{s.label}</div>
                </div>
              ))}
            </div>
          </div>
          <div className="rounded-sm border border-dashed border-primary/40 bg-primary/5 p-4">
            <div className="flex items-center gap-2 mb-2">
              <Send className="h-4 w-4 text-primary" />
              <span className="text-sm font-semibold">Test Mode</span>
              <span className="text-xs text-muted-foreground">— send the full 3-email sequence to yourself before inviting pilots.</span>
            </div>
            <div className="flex flex-col sm:flex-row gap-2">
              <Input
                type="email"
                value={dripEmail}
                onChange={(e) => setDripEmail(e.target.value)}
                placeholder="your@email.com (defaults to your admin email)"
                className="rounded-sm"
                data-testid="drip-test-email-input"
              />
              <Button onClick={sendTestSequence} disabled={dripSending || !gmail?.connected}
                className="rounded-sm shrink-0" data-testid="drip-send-test-button">
                {dripSending ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <Send className="h-4 w-4 mr-2" />}
                Send test sequence
              </Button>
            </div>
          </div>
        </div>

        {activation && (
          <div className="p-5 rounded-sm border border-border/70 bg-card mb-8" data-testid="activation-funnel-card">
            <div className="flex items-center gap-2 mb-1">
              <TrendingUp className="h-4 w-4 text-primary" />
              <h3 className="font-display font-bold">Activation Funnel</h3>
            </div>
            <p className="text-xs text-muted-foreground mb-5">
              Where people are in the journey — from lead to paid student. This is exactly where you're losing people.
            </p>
            {(() => {
              const stages = [
                { label: "Landing Page Leads", value: activation.leads_total,
                  note: `${activation.leads_converted} became students (${activation.leads_conversion_pct}%)` },
                { label: "Signed Up", value: activation.signed_up },
                { label: "Logged In", value: activation.logged_in, note: "tracked going forward" },
                { label: "Started Lesson 1", value: activation.started_lesson_1, note: "tracked going forward" },
                { label: "Completed Lesson 1", value: activation.completed_lesson_1 },
                { label: "Completed Golden Five", value: activation.completed_golden_five, highlight: true },
                { label: "Founder", value: activation.paid },
              ];
              const max = Math.max(1, ...stages.map((s) => s.value || 0));
              return (
                <div className="space-y-2.5">
                  {stages.map((s, i) => {
                    const pct = Math.round(((s.value || 0) / max) * 100);
                    return (
                      <div key={s.label} className="flex items-center gap-3" data-testid={`activation-stage-${i}`}>
                        <span className="text-xs sm:text-sm w-40 sm:w-48 shrink-0 font-mono text-slate-300 truncate">{s.label}</span>
                        <div className="flex-1 h-6 rounded-sm bg-secondary/60 overflow-hidden relative">
                          <div className={`h-full ${s.highlight ? "bg-primary" : "bg-primary/50"}`} style={{ width: `${pct}%` }} />
                        </div>
                        <span className={`font-mono text-sm w-10 text-right ${s.highlight ? "text-primary font-bold" : ""}`}>{s.value ?? "—"}</span>
                        {s.note && <span className="text-[10px] text-muted-foreground w-40 hidden lg:block">{s.note}</span>}
                      </div>
                    );
                  })}
                </div>
              );
            })()}
          </div>
        )}

        {quizAnalytics && quizAnalytics.lessons.length > 0 && (
          <div className="p-5 rounded-sm border border-border/70 bg-card mb-8" data-testid="quiz-analytics-card">
            <div className="flex items-center gap-2 mb-1">
              <TrendingUp className="h-4 w-4 text-accent" />
              <h3 className="font-display font-bold">Lesson Quiz Analytics</h3>
            </div>
            <p className="text-xs text-muted-foreground mb-4">
              Overall pass rate <span className="text-accent font-mono">{quizAnalytics.summary.overall_pass_rate}%</span> ·
              {" "}{quizAnalytics.summary.students_quizzed} students · {quizAnalytics.summary.total_attempts} attempts. Low pass rates flag confusing content.
            </p>
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="text-left text-[10px] font-mono uppercase tracking-widest text-muted-foreground border-b border-border/60">
                    <th className="py-2 pr-3">Lesson</th>
                    <th className="py-2 px-3 text-center">Attempts</th>
                    <th className="py-2 px-3 text-center">Avg Score</th>
                    <th className="py-2 px-3 text-center">Pass Rate</th>
                    <th className="py-2 pl-3">Most Missed Question</th>
                  </tr>
                </thead>
                <tbody>
                  {quizAnalytics.lessons.map((l, i) => (
                    <tr key={l.lesson_id} className="border-b border-border/40" data-testid={`quiz-analytics-row-${i}`}>
                      <td className="py-2.5 pr-3 font-medium">{l.title.replace(" — Knowledge Check", "")}</td>
                      <td className="py-2.5 px-3 text-center font-mono">{l.total_attempts}</td>
                      <td className="py-2.5 px-3 text-center font-mono">{l.avg_score}%</td>
                      <td className={`py-2.5 px-3 text-center font-mono ${l.pass_rate < 60 ? "text-rose-400" : "text-primary"}`}>{l.pass_rate}%</td>
                      <td className="py-2.5 pl-3 text-xs text-muted-foreground max-w-xs">{l.most_missed_question ? `${l.most_missed_question} (${l.most_missed_count}×)` : "—"}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}

        {badgeStats && (
          <div className="p-5 rounded-sm border border-border/70 bg-card mb-8" data-testid="badge-analytics-card">
            <div className="flex items-center gap-2 mb-1">
              <Award className="h-4 w-4 text-primary" />
              <h3 className="font-display font-bold">Achievement Badges</h3>
            </div>
            <p className="text-xs text-muted-foreground mb-4">
              {badgeStats.total_awarded} badges awarded across {badgeStats.students_with_progress} active students.
            </p>
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
              {badgeStats.badges.map((b, i) => {
                const Icon = getIcon(b.icon);
                return (
                  <div key={b.id} className="p-4 rounded-sm border border-border/60 bg-secondary/20 text-center" data-testid={`badge-stat-${i}`}>
                    <Icon className="h-6 w-6 mx-auto mb-2 text-primary" />
                    <div className="text-xs font-medium leading-tight">{b.name}</div>
                    <div className="font-mono text-2xl font-bold mt-2">{b.earned_count}</div>
                    <div className="text-[10px] uppercase tracking-widest text-muted-foreground mt-0.5">
                      earned · {b.earned_pct}%
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        )}

        {loading ? (
          <div className="flex items-center justify-center h-[40vh]"><Loader2 className="h-8 w-8 animate-spin text-primary" /></div>
        ) : (
          <>
            <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-4 mb-8">
              {stats.map((s, i) => (
                <motion.div key={s.label} initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: i * 0.06 }}
                  className="p-5 rounded-sm border border-border/70 bg-card" data-testid={`admin-stat-${i}`}>
                  <s.icon className="h-5 w-5 text-primary mb-4" />
                  <div className="font-mono text-2xl font-bold">{s.value}</div>
                  <div className="text-xs uppercase tracking-widest text-muted-foreground mt-1">{s.label}</div>
                </motion.div>
              ))}
            </div>

            {Object.keys(data.summary.revenue_by_campaign || {}).length > 0 && (
              <div className="p-5 rounded-sm border border-border/70 bg-card mb-8" data-testid="revenue-by-campaign">
                <div className="flex items-center gap-2 mb-4">
                  <TrendingUp className="h-4 w-4 text-primary" />
                  <h3 className="font-display font-bold">Revenue by Campaign</h3>
                </div>
                <div className="space-y-2">
                  {Object.entries(data.summary.revenue_by_campaign)
                    .sort((a, b) => b[1] - a[1])
                    .map(([camp, amt]) => {
                      const pct = data.summary.revenue > 0 ? Math.round((amt / data.summary.revenue) * 100) : 0;
                      return (
                        <div key={camp} className="flex items-center gap-3" data-testid={`campaign-row-${camp}`}>
                          <span className="text-sm w-40 truncate font-mono text-slate-300">{camp}</span>
                          <div className="flex-1 h-2 rounded-sm bg-secondary overflow-hidden">
                            <div className="h-full bg-primary" style={{ width: `${pct}%` }} />
                          </div>
                          <span className="font-mono text-sm text-primary w-20 text-right">${amt.toFixed(2)}</span>
                        </div>
                      );
                    })}
                </div>
              </div>
            )}

            {funnel.length > 0 && (
              <div className="p-5 rounded-sm border border-border/70 bg-card mb-8" data-testid="campaign-funnel">
                <div className="flex items-center gap-2 mb-4">
                  <Target className="h-4 w-4 text-primary" />
                  <h3 className="font-display font-bold">Lead → Sale Funnel by Campaign</h3>
                </div>
                <Table>
                  <TableHeader>
                    <TableRow className="border-border/60 hover:bg-transparent">
                      <TableHead className="font-mono text-xs uppercase tracking-widest">Campaign</TableHead>
                      <TableHead className="font-mono text-xs uppercase tracking-widest text-right">Leads</TableHead>
                      <TableHead className="font-mono text-xs uppercase tracking-widest text-right">Sales</TableHead>
                      <TableHead className="font-mono text-xs uppercase tracking-widest text-right">Conv %</TableHead>
                      <TableHead className="font-mono text-xs uppercase tracking-widest text-right">Revenue</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {funnel.map((f, i) => (
                      <TableRow key={f.camp} className="border-border/50" data-testid={`funnel-row-${i}`}>
                        <TableCell className="text-sm font-mono text-slate-300 truncate max-w-[220px]">{f.camp}</TableCell>
                        <TableCell className="text-right font-mono text-sm">{f.leads}</TableCell>
                        <TableCell className="text-right font-mono text-sm text-primary">{f.sales}</TableCell>
                        <TableCell className="text-right font-mono text-sm">{f.conversion_pct}%</TableCell>
                        <TableCell className="text-right font-mono text-sm text-primary">${f.revenue.toFixed(2)}</TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </div>
            )}

            {analytics && analytics.lessons.length > 0 && (
              <div className="p-5 rounded-sm border border-border/70 bg-card mb-8" data-testid="lesson-analytics">
                <div className="flex items-center gap-2 mb-1">
                  <Clock className="h-4 w-4 text-primary" />
                  <h3 className="font-display font-bold">Lesson Pulse & Timing (Beta)</h3>
                </div>
                <p className="text-xs text-muted-foreground mb-4">
                  "Was this lesson clear?" responses and actual time-on-lesson vs. the estimate. Watch for lessons finishing far under or over their estimate.
                </p>
                <Table>
                  <TableHeader>
                    <TableRow className="border-border/60 hover:bg-transparent">
                      <TableHead className="font-mono text-xs uppercase tracking-widest">Lesson</TableHead>
                      <TableHead className="font-mono text-xs uppercase tracking-widest text-right">Responses</TableHead>
                      <TableHead className="font-mono text-xs uppercase tracking-widest text-right">Clear %</TableHead>
                      <TableHead className="font-mono text-xs uppercase tracking-widest text-right">Avg Time</TableHead>
                      <TableHead className="font-mono text-xs uppercase tracking-widest text-right">Estimate</TableHead>
                      <TableHead className="font-mono text-xs uppercase tracking-widest text-right">Δ</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {analytics.lessons.map((l, i) => {
                      const fmt = (s) => (s == null ? "—" : `${Math.floor(s / 60)}:${String(s % 60).padStart(2, "0")}`);
                      const estSec = l.estimate_min ? l.estimate_min * 60 : null;
                      const delta = l.avg_seconds != null && estSec ? l.avg_seconds - estSec : null;
                      const deltaCls = delta == null ? "text-muted-foreground"
                        : Math.abs(delta) <= 90 ? "text-primary"
                        : "text-accent";
                      return (
                        <TableRow key={l.lesson_id} className="border-border/50" data-testid={`analytics-row-${i}`}>
                          <TableCell className="text-sm font-medium max-w-[240px] truncate">{l.title}</TableCell>
                          <TableCell className="text-right font-mono text-sm">
                            {l.responses}{l.responses > 0 && <span className="text-muted-foreground text-xs"> ({l.clear_yes}/{l.clear_no})</span>}
                          </TableCell>
                          <TableCell className="text-right font-mono text-sm">
                            {l.clarity_pct == null ? "—" : <span className={l.clarity_pct >= 80 ? "text-primary" : "text-accent"}>{l.clarity_pct}%</span>}
                          </TableCell>
                          <TableCell className="text-right font-mono text-sm">{fmt(l.avg_seconds)}</TableCell>
                          <TableCell className="text-right font-mono text-sm text-muted-foreground">{l.estimate_min ? `${l.estimate_min}:00` : "—"}</TableCell>
                          <TableCell className={`text-right font-mono text-sm ${deltaCls}`}>
                            {delta == null ? "—" : `${delta >= 0 ? "+" : "−"}${fmt(Math.abs(delta))}`}
                          </TableCell>
                        </TableRow>
                      );
                    })}
                  </TableBody>
                </Table>
              </div>
            )}

            {feedback && feedback.feedback.length > 0 && (
              <div className="p-5 rounded-sm border border-border/70 bg-card mb-8" data-testid="beta-feedback-table">
                <div className="flex items-center gap-2 mb-1">
                  <MessageSquareText className="h-4 w-4 text-primary" />
                  <h3 className="font-display font-bold">Beta Feedback</h3>
                  <span className="text-xs font-mono text-muted-foreground ml-2">
                    {feedback.total} total · {feedback.curriculum} curriculum · {feedback.platform} platform
                  </span>
                </div>
                <p className="text-xs text-muted-foreground mb-4">Tester notes from the nav form and the Golden Five onboarding survey.</p>
                <div className="max-h-[420px] overflow-y-auto">
                  <Table>
                    <TableHeader>
                      <TableRow className="border-border/60 hover:bg-transparent">
                        <TableHead className="font-mono text-xs uppercase tracking-widest">When</TableHead>
                        <TableHead className="font-mono text-xs uppercase tracking-widest">Category</TableHead>
                        <TableHead className="font-mono text-xs uppercase tracking-widest">Type</TableHead>
                        <TableHead className="font-mono text-xs uppercase tracking-widest">Message</TableHead>
                        <TableHead className="font-mono text-xs uppercase tracking-widest">From</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {feedback.feedback.map((f, i) => (
                        <TableRow key={i} className="border-border/50 align-top" data-testid={`feedback-row-${i}`}>
                          <TableCell className="text-xs text-muted-foreground whitespace-nowrap">{fmtDate(f.created_at)}</TableCell>
                          <TableCell>
                            <span className={`inline-block text-[10px] font-mono uppercase tracking-widest px-2 py-1 rounded-sm border ${
                              f.category === "curriculum" ? "text-primary border-primary/40 bg-primary/10" : "text-accent border-accent/40 bg-accent/10"}`}>
                              {f.category}
                            </span>
                          </TableCell>
                          <TableCell className="text-xs text-slate-400 max-w-[160px]">{f.type || "—"}</TableCell>
                          <TableCell className="text-sm max-w-[320px]">
                            <div className="line-clamp-3">{f.message}</div>
                            {f.context && <div className="text-[11px] text-muted-foreground mt-1 font-mono line-clamp-2">{f.context}</div>}
                          </TableCell>
                          <TableCell className="text-xs text-muted-foreground">{f.user_email || "—"}</TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </div>
              </div>
            )}

            <div className="rounded-sm border border-border/70 bg-card overflow-hidden" data-testid="admin-transactions-table">
              <Table>
                <TableHeader>
                  <TableRow className="border-border/60 hover:bg-transparent">
                    <TableHead className="font-mono text-xs uppercase tracking-widest">Date</TableHead>
                    <TableHead className="font-mono text-xs uppercase tracking-widest">Customer</TableHead>
                    <TableHead className="font-mono text-xs uppercase tracking-widest">Campaign</TableHead>
                    <TableHead className="font-mono text-xs uppercase tracking-widest">Amount</TableHead>
                    <TableHead className="font-mono text-xs uppercase tracking-widest">Status</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {data.transactions.length === 0 ? (
                    <TableRow>
                      <TableCell colSpan={5} className="text-center text-muted-foreground py-10">No transactions yet.</TableCell>
                    </TableRow>
                  ) : (
                    data.transactions.map((t, i) => (
                      <TableRow key={t.order_id} className="border-border/50" data-testid={`admin-txn-${i}`}>
                        <TableCell className="text-sm text-muted-foreground whitespace-nowrap">{fmtDate(t.created_at)}</TableCell>
                        <TableCell>
                          <div className="text-sm font-medium">{t.user_name}</div>
                          <div className="text-xs text-muted-foreground">{t.user_email}</div>
                        </TableCell>
                        <TableCell className="text-sm">
                          {t.campaign ? <span className="text-primary">{t.campaign}</span>
                            : t.utm_source ? <span className="text-slate-400 font-mono text-xs">{t.utm_source}</span>
                            : t.gclid ? <span className="text-accent font-mono text-xs">Google Ads</span>
                            : <span className="text-muted-foreground">direct</span>}
                        </TableCell>
                        <TableCell className="font-mono text-sm">${t.captured_amount || t.amount} {t.currency}</TableCell>
                        <TableCell>
                          <span className={`inline-block text-[10px] font-mono uppercase tracking-widest px-2 py-1 rounded-sm border ${STATUS_STYLES[t.status] || STATUS_STYLES.CREATED}`}>
                            {t.status}
                          </span>
                        </TableCell>
                      </TableRow>
                    ))
                  )}
                </TableBody>
              </Table>
            </div>
          </>
        )}
      </main>
    </div>
  );
}
